import { Response } from 'common/src/code/utils/CommunicationTypes';
import React from 'react';
import Modal from '../../modal';
import Utils from '../../utils';

const { ModalButtonActionType, ModalContext, StatusModal } = Modal;
const { Communication } = Utils;

type FedExDisconnectProps = {
	readonly modalId: string;
	readonly carrier: {
		readonly value: string;
		readonly displayName: string;
	};
	readonly completionCallback: (success: boolean) => void;
};

export function FedExDisconnect({
	modalId,
	carrier,
	completionCallback,
}: FedExDisconnectProps) {
	const { showModal, showError, closeModal } = React.useContext(ModalContext);
	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	const deleteFedex = async (): Promise<void> => {
		setIsLoading(true);
		const response: Response = await Communication.deleteCarrier({
			Carrier: carrier.value,
		});
		closeModal(modalId);

		if (Communication.isSuccessful(response)) {
			showModal({
				component: ({ modalId }) => (
					<StatusModal
						modalId={modalId}
						header="Carrier Disconnected"
						description={`${carrier.displayName} has been successfully disconnected from your account.`}
						onClose={() => {
							closeModal(modalId, ModalButtonActionType.Ok);
							completionCallback(true);
						}}
						dataqa="fedex-disconnected-modal"
						iconType="success"
					/>
				),
			});
		} else {
			showError({
				data: {
					title: 'Connection Error',
					description:
						'<p><b>Your FedEx account could not be disconnected.</b></p><p>If you keep receiving this error, call support at <b>1-888-434-0055</b> (available Monday - Friday from 8am - 8pm CT).</p>',
					primaryButtonText: 'OK',
					handleType: 'custom',
				},
			});
		}
	};

	return (
		<StatusModal
			modalId={modalId}
			header="Disconnect Carrier"
			title={`Are you sure you want to disconnect ${carrier.displayName}?`}
			description="To use this carrier’s services in the future you will
						need to add it again."
			onClose={() => {
				closeModal(modalId, ModalButtonActionType.Ok);
				completionCallback(true);
			}}
			primaryButton={{
				type: ModalButtonActionType.Confirm,
				text: isLoading ? 'Disconnecting...' : 'Disconnect',
				onClick: async () => {
					await deleteFedex();
				},
				disabled: isLoading,
				loading: isLoading,
			}}
			secondaryButton={{
				type: ModalButtonActionType.Cancel,
				buttonType: 'tertiary-ghost',
				text: 'Cancel',
				onClick: () => {
					closeModal(modalId, ModalButtonActionType.Cancel);
				},
			}}
			dataqa="fedex-disconnect-modal"
			iconType="warning"
		/>
	);
}
